import React, { useState, useEffect } from 'react';
import { MapPin, Clock, CheckCircle, LogIn, LogOut as LogOutIcon } from 'lucide-react'; // eslint-disable-line no-unused-vars
import { safeFetch, extractError } from '../../lib/safeFetch';
import { getPreciseLocation } from '../../lib/preciseLocation';
import { startGpsTracking, stopGpsTracking, flushGpsQueue } from '../../lib/gpsTracker';
import GateOtpModal from './GateOtpModal';
import LocationVerifyingModal from './LocationVerifyingModal';
import CheckinReasonModal from './CheckinReasonModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compute current IST time vs scheduled time. Accepts:
//   • "HH:MM"      — standard
//   • "0.375"      — Excel fraction-of-day (legacy import shape)
//   • 540          — minutes-since-midnight (defensive)
// Returns delta in minutes (positive = AFTER schedule). null when unparseable.
const minutesVsSchedule = (schedule) => {
  if (schedule == null || schedule === '') return null;
  let schedH = null;
  let schedMin = null;
  const s = String(schedule).trim();
  if (s.includes(':')) {
    const m = s.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    schedH = parseInt(m[1], 10);
    schedMin = parseInt(m[2], 10);
  } else {
    const n = parseFloat(s);
    if (!Number.isFinite(n) || n < 0) return null;
    let totalMin;
    if (n < 1) totalMin = Math.round(n * 24 * 60);       // Excel fraction
    else if (n < 1440) totalMin = Math.floor(n);          // minutes since midnight
    else return null;
    schedH = Math.floor(totalMin / 60);
    schedMin = totalMin % 60;
  }
  if (schedH < 0 || schedH > 23 || schedMin < 0 || schedMin > 59) return null;
  const istNow = new Date(Date.now() + 5.5 * 3600 * 1000);
  const curH = istNow.getUTCHours();
  const curM = istNow.getUTCMinutes();
  return (curH * 60 + curM) - (schedH * 60 + schedMin);
};

export default function CheckInOut() {
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [permissionOk, setPermissionOk] = useState(false);
  const [otpModal, setOtpModal] = useState(null);
  // Verifying-location overlay state. `null` = hidden.
  const [verifying, setVerifying] = useState(null); // { action, taken, lastAccuracy }
  // Late/Early reason modal. `null` = hidden. Filled with
  //   { mode:'checkin'|'checkout', minutesOff, scheduled, loc }
  // — `loc` is the already-collected precise-location payload so we don't
  // re-prompt for GPS after the staff picks a reason.
  const [reasonModal, setReasonModal] = useState(null);

  useEffect(() => {
    fetchTodayAttendance();
    // Just prime the GPS permission prompt — the real readings happen on tap.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => setPermissionOk(true),
        () => setPermissionOk(false),
        { enableHighAccuracy: false, maximumAge: 60000, timeout: 5000 }
      );
    }
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/attendance/today`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAttendance(data);
    } catch (err) {
      console.error('Failed to fetch attendance:', err);
    }
  };

  const collectLocationOrToast = async (action) => {
    setVerifying({ action, taken: 0, lastAccuracy: null });
    try {
      const loc = await getPreciseLocation({
        onProgress: (taken, _total, lastSample) => {
          setVerifying((v) => v && { ...v, taken, lastAccuracy: lastSample?.accuracy ?? v.lastAccuracy });
        },
      });
      return loc;
    } catch (e) {
      alert(e?.message || 'Location required — please enable Location services.');
      return null;
    } finally {
      setVerifying(null);
    }
  };

  const handleCheckIn = async () => {
    if (loading) return;
    setLoading(true);
    const loc = await collectLocationOrToast('Check-in');
    if (!loc) { setLoading(false); return; }
    // Late check-in? Pop the reason modal first; the actual POST happens
    // after the staff picks a reason in submitCheckInOut().
    const userObj = JSON.parse(localStorage.getItem('user') || '{}');
    const delta = minutesVsSchedule(userObj.day_checkin_time);
    if (delta != null && delta > 0) {
      setReasonModal({
        mode: 'checkin',
        minutesOff: delta,
        scheduled: userObj.day_checkin_time,
        loc,
      });
      setLoading(false); // modal owns the busy state from here
      return;
    }
    await submitCheckIn(loc, null);
  };

  const handleCheckOut = async () => {
    if (loading) return;
    setLoading(true);
    const loc = await collectLocationOrToast('Check-out');
    if (!loc) { setLoading(false); return; }
    const userObj = JSON.parse(localStorage.getItem('user') || '{}');
    const delta = minutesVsSchedule(userObj.day_checkout_time);
    // Early = current IST < scheduled. delta is negative in that case.
    if (delta != null && delta < 0) {
      setReasonModal({
        mode: 'checkout',
        minutesOff: -delta,
        scheduled: userObj.day_checkout_time,
        loc,
      });
      setLoading(false);
      return;
    }
    await submitCheckOut(loc, null);
  };

  // Actual POSTs — accept an optional reason payload from the modal.
  const submitCheckIn = async (loc, reason) => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const body = { ...loc, ...(reason || {}) };
    const result = await safeFetch(`${API_URL}/api/attendance/checkin`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (result.ok) {
      // Fire-and-forget: don't let a slow native GPS startup delay the OTP
      // modal or the "checked in" state refresh. v2.4.3 hotfix — user
      // reported check-in appearing slow AND dashboard showing "not
      // checked in" after a successful call. Root cause: this line used
      // to ``await startGpsTracking`` which can hang when the native
      // background-geolocation plugin is prompting for the new
      // ACCESS_BACKGROUND_LOCATION permission — everything below then
      // waited for that dialog. Now startGpsTracking runs in the
      // background; the OTP shows and the dashboard refreshes immediately.
      startGpsTracking({ intervalSeconds: 30 }).catch(() => { /* best-effort */ });
      setOtpModal({
        otp: result.data?.entry_otp,
        label: 'Check-in',
        subtitle: result.data?.inside_geofence ? 'Inside office geofence' : 'Outside office — pending approval',
        autoApproved: result.data?.auto_approved,
      });
      fetchTodayAttendance();
    } else {
      const handled = await handleAlreadyCheckedIn(result, 'in');
      if (!handled) alert(extractError(result, 'Check-in failed'));
    }
    setReasonModal(null);
    setLoading(false);
  };

  const submitCheckOut = async (loc, reason) => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const body = { ...loc, ...(reason || {}) };
    const result = await safeFetch(`${API_URL}/api/attendance/checkout`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (result.ok) {
      // Fire-and-forget the GPS teardown so a slow native stopWatcher call
      // can't stall the OTP modal or attendance refresh (mirror of the
      // v2.4.3 check-in hotfix — same hang symptom, same fix).
      flushGpsQueue().catch(() => {});
      stopGpsTracking().catch(() => {});
      setOtpModal({
        otp: result.data?.exit_otp,
        label: 'Check-out',
        subtitle: `Total hours today: ${result.data?.total_hours}h · GPS tracking stopped`,
        autoApproved: result.data?.auto_approved,
      });
      fetchTodayAttendance();
    } else {
      const handled = await handleAlreadyCheckedIn(result, 'out');
      if (!handled) alert(extractError(result, 'Check-out failed'));
    }
    setReasonModal(null);
    setLoading(false);
  };

  // Helper: when a 400 comes back without a useful body (Cloudflare/WebView
  // ate the JSON detail), re-fetch attendance and check whether the action
  // actually succeeded. If so, swallow the error silently. Returns true when
  // it has handled the case.
  const handleAlreadyCheckedIn = async (result, intent) => {
    if (result.status !== 400) return false;
    const detail = (result.data?.detail || result.text || '').toString().toLowerCase();
    if (detail.includes('already checked in') && intent === 'in') {
      await fetchTodayAttendance();
      return true;
    }
    if (detail.includes('already checked out') && intent === 'out') {
      await fetchTodayAttendance();
      return true;
    }
    if (!detail) {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/attendance/today`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          const openNow = d?.has_open_session ?? !!(d?.check_in && !d?.check_out);
          if (intent === 'in' && openNow) {
            setAttendance(d);
            return true;
          }
          if (intent === 'out' && !openNow && d?.check_out) {
            setAttendance(d);
            return true;
          }
        }
      } catch {}
    }
    return false;
  };

  const hasOpenSession = attendance?.has_open_session ?? !!(attendance?.check_in && !attendance?.check_out);
  const isCheckedIn = hasOpenSession;
  const stateLoaded = attendance !== null;
  const dayHasActivity = !!attendance?.check_in;
  const sessionCount = attendance?.session_count ?? (attendance?.check_in ? 1 : 0);

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-blue-100 text-sm">Today's Status</p>
            <p className="text-2xl font-bold mt-1">
              {!stateLoaded
                ? 'Loading…'
                : (isCheckedIn ? 'Checked In' : (dayHasActivity ? 'Checked Out' : 'Not Started'))}
            </p>
            {sessionCount > 1 && (
              <p className="text-blue-100 text-xs mt-1" data-testid="ci-session-count">
                Session {sessionCount}{isCheckedIn ? ' (open)' : ' (closed)'}
              </p>
            )}
          </div>
          <CheckCircle size={48} className="opacity-80" />
        </div>
        {attendance?.check_in && (
          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2">
              <Clock size={16} />
              <span>First In: {new Date(attendance.check_in.time).toLocaleTimeString('en-IN')}</span>
            </div>
            {attendance.check_out && !isCheckedIn && (
              <div className="flex items-center space-x-2">
                <Clock size={16} />
                <span>Last Out: {new Date(attendance.check_out.time).toLocaleTimeString('en-IN')}</span>
              </div>
            )}
            {attendance.total_hours > 0 && !isCheckedIn && (
              <div className="text-blue-100 text-xs">Total so far: {attendance.total_hours}h</div>
            )}
          </div>
        )}
      </div>

      {!permissionOk && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-orange-800 text-sm">
          📍 Location permission required — please allow when prompted.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={handleCheckIn}
          disabled={loading || !stateLoaded || isCheckedIn}
          className={`font-semibold py-4 px-4 rounded-xl shadow-lg transition-all flex items-center justify-center space-x-2 ${
            !stateLoaded || isCheckedIn
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
          data-testid="checkin-btn"
        >
          <LogIn size={22} />
          <span className="text-base">
            {loading && !isCheckedIn ? 'Checking in…' : (dayHasActivity && !isCheckedIn ? 'Check In Again' : 'Check In')}
          </span>
        </button>
        <button
          onClick={handleCheckOut}
          disabled={loading || !stateLoaded || !isCheckedIn}
          className={`font-semibold py-4 px-4 rounded-xl shadow-lg transition-all flex items-center justify-center space-x-2 ${
            !stateLoaded || !isCheckedIn
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-red-600 hover:bg-red-700 text-white'
          }`}
          data-testid="checkout-btn"
        >
          <LogOutIcon size={22} />
          <span className="text-base">{loading && isCheckedIn ? 'Checking out…' : 'Check Out'}</span>
        </button>
      </div>

      {verifying && (
        <LocationVerifyingModal
          action={verifying.action}
          taken={verifying.taken}
          lastAccuracy={verifying.lastAccuracy}
        />
      )}

      {otpModal && (
        <GateOtpModal
          otp={otpModal.otp}
          label={otpModal.label}
          subtitle={otpModal.subtitle}
          autoApproved={otpModal.autoApproved}
          onClose={() => setOtpModal(null)}
        />
      )}

      {reasonModal && (
        <CheckinReasonModal
          mode={reasonModal.mode}
          minutesOff={reasonModal.minutesOff}
          scheduled={reasonModal.scheduled}
          submitting={loading}
          onCancel={() => setReasonModal(null)}
          onConfirm={(payload) => {
            if (reasonModal.mode === 'checkin') {
              submitCheckIn(reasonModal.loc, payload);
            } else {
              submitCheckOut(reasonModal.loc, payload);
            }
          }}
        />
      )}
    </div>
  );
}
